import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/layout/MainLayout';
import { marketplaceService, EbayConversation } from '../../api/services/marketplaceService';
import { toast } from 'react-hot-toast';
import {
  MessageSquare,
  RefreshCw,
  Search,
  ChevronLeft,
  Reply,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  Mail,
  MailOpen,
  ShoppingBag,
  Link2Off,
  Loader2,
  Paperclip,
} from 'lucide-react';

type Platform = 'ebay' | 'amazon';
type MessageFilter = 'all' | 'unread' | 'replied' | 'awaiting';
type MessageSource = 'customer' | 'system';

interface MarketplaceMessage {
  id: string;
  platform: Platform;
  subject: string;
  senderName: string;
  senderId: string;
  body: string;
  receivedAt: string;
  isRead: boolean;
  isReplied: boolean;
  orderId?: string;
  itemTitle?: string;
  isSystemMessage: boolean;
}

// Placeholder Amazon messages — Amazon SP-API integration pending
const AMAZON_PLACEHOLDER: MarketplaceMessage[] = [
  {
    id: 'amz-001',
    platform: 'amazon',
    subject: 'Return request - wrong color received',
    senderName: 'Amazon Customer',
    senderId: 'A2XYZ123456789',
    body: 'I ordered the seat covers in tan but received them in grey. I need to return and get the correct color.',
    receivedAt: '2026-04-22T11:00:00Z',
    isRead: false,
    isReplied: false,
    orderId: '114-1234567-8901234',
    itemTitle: 'CalTrend Genuine Leather Seat Covers',
    isSystemMessage: false,
  },
  {
    id: 'amz-002',
    platform: 'amazon',
    subject: 'Installation help needed',
    senderName: 'Amazon Customer',
    senderId: 'A3ABC987654321',
    body: 'The seat covers look great but I am having trouble getting the bottom cover to attach. Is there a video guide?',
    receivedAt: '2026-04-20T16:45:00Z',
    isRead: true,
    isReplied: false,
    itemTitle: 'CalTrend Sport-X Seat Covers',
    isSystemMessage: false,
  },
];

const PLATFORM_CFG = {
  ebay: {
    label: 'eBay',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    btnBg: 'bg-blue-600 hover:bg-blue-700',
  },
  amazon: {
    label: 'Amazon',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    btnBg: 'bg-orange-600 hover:bg-orange-700',
  },
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function MarketplaceMessages() {
  const [activePlatform, setActivePlatform] = useState<Platform>('ebay');
  const [messageSource, setMessageSource] = useState<MessageSource>('customer');
  const [activeFilter, setActiveFilter] = useState<MessageFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversationKey, setSelectedConversationKey] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<MarketplaceMessage | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [connectingEbay, setConnectingEbay] = useState(false);
  const popupRef = useRef<Window | null>(null);

  const queryClient = useQueryClient();

  const { data: ebayStatus, isLoading: ebayStatusLoading } = useQuery({
    queryKey: ['marketplace', 'ebay', 'status'],
    queryFn: marketplaceService.getEbayStatus,
    refetchInterval: 30000,
  });

  const ebayConnected = ebayStatus?.connected ?? false;

  const { data: ebayMessagesData, isError: ebayMessagesError } = useQuery({
    queryKey: ['marketplace', 'ebay', 'messages'],
    queryFn: marketplaceService.getEbayMessages,
    enabled: ebayConnected,
    refetchInterval: 60000,
  });

  const { data: ebayConversationsData, isError: ebayConversationsError } = useQuery({
    queryKey: ['marketplace', 'ebay', 'conversations'],
    queryFn: marketplaceService.getEbayConversations,
    enabled: ebayConnected,
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (ebayMessagesError || ebayConversationsError) toast.error('Failed to load eBay messages');
  }, [ebayMessagesError, ebayConversationsError]);

  const disconnectEbayMutation = useMutation({
    mutationFn: marketplaceService.disconnectEbay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'ebay'] });
      toast.success('eBay account disconnected');
    },
    onError: () => toast.error('Failed to disconnect eBay'),
  });

  const replyMutation = useMutation({
    mutationFn: marketplaceService.replyToEbayMessage,
    onSuccess: () => {
      setReplyText('');
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'ebay', 'conversations'] });
      toast.success('Reply sent to eBay');
    },
    onError: () => toast.error('Failed to send reply to eBay'),
  });

  // Listen for the OAuth popup result
  const handlePopupMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type !== 'EBAY_AUTH_RESULT') return;
    setConnectingEbay(false);
    if (event.data.status === 'success') {
      toast.success('eBay account connected!');
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'ebay'] });
    } else {
      toast.error('eBay authorization failed. Please try again.');
    }
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener('message', handlePopupMessage);
    return () => window.removeEventListener('message', handlePopupMessage);
  }, [handlePopupMessage]);

  // Detect if the popup was closed manually without completing OAuth
  useEffect(() => {
    if (!connectingEbay) return;
    const interval = setInterval(() => {
      if (popupRef.current?.closed) {
        setConnectingEbay(false);
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [connectingEbay]);

  const handleConnectEbay = async () => {
    try {
      setConnectingEbay(true);
      const url = await marketplaceService.getEbayAuthUrl();
      const popup = window.open(
        url,
        'ebay_oauth',
        'width=600,height=700,left=' +
          Math.round(window.screenX + (window.outerWidth - 600) / 2) +
          ',top=' +
          Math.round(window.screenY + (window.outerHeight - 700) / 2)
      );
      if (!popup) {
        toast.error('Popup was blocked. Please allow popups for this site and try again.');
        setConnectingEbay(false);
        return;
      }
      popupRef.current = popup;
    } catch {
      toast.error('Could not load eBay authorization URL. Check that EBAY_CLIENT_ID is configured.');
      setConnectingEbay(false);
    }
  };

  const ebaySystemMessages: MarketplaceMessage[] = (ebayMessagesData ?? [])
    .filter(m => m.isSystemMessage)
    .map(m => ({
      id: m.id,
      platform: 'ebay',
      subject: m.subject,
      senderName: m.senderName,
      senderId: m.senderId,
      body: m.body,
      receivedAt: m.receivedAt,
      isRead: m.isRead,
      isReplied: false,
      itemTitle: m.itemTitle,
      isSystemMessage: true,
    }));
  const amazonMessages = AMAZON_PLACEHOLDER;
  const conversations = ebayConversationsData ?? [];

  const isCustomerView = activePlatform === 'ebay' && messageSource === 'customer';

  const ebayUnreadCount =
    conversations.reduce((sum, c) => sum + c.turns.filter(t => t.direction === 'incoming' && !t.isRead).length, 0) +
    ebaySystemMessages.filter(m => !m.isRead).length;

  const flatMessages = activePlatform === 'ebay' ? ebaySystemMessages : amazonMessages;

  const filteredConversations = conversations
    .filter(c => {
      if (activeFilter === 'unread' && !c.hasUnread) return false;
      const lastTurn = c.turns[c.turns.length - 1];
      if (activeFilter === 'awaiting' && lastTurn?.direction !== 'incoming') return false;
      if (activeFilter === 'replied' && lastTurn?.direction !== 'outgoing') return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          c.buyerUsername.toLowerCase().includes(q) ||
          (c.itemTitle ?? '').toLowerCase().includes(q) ||
          c.turns.some(t => t.body.toLowerCase().includes(q))
        );
      }
      return true;
    })
    .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

  const filteredMessages = flatMessages
    .filter(m => {
      if (activeFilter === 'unread' && m.isRead) return false;
      if (activeFilter === 'replied' && !m.isReplied) return false;
      if (activeFilter === 'awaiting' && (m.isReplied || !m.isRead)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          m.subject.toLowerCase().includes(q) ||
          m.senderName.toLowerCase().includes(q) ||
          m.body.toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime());

  const handleRefresh = () => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ['marketplace'] });
    setTimeout(() => setIsRefreshing(false), 1200);
  };

  const cfg = PLATFORM_CFG[activePlatform];
  const isConnected = activePlatform === 'ebay' ? ebayConnected : false;

  const selectedConversation: EbayConversation | null =
    conversations.find(c => c.key === selectedConversationKey) ?? null;
  const lastIncomingTurn = selectedConversation
    ? [...selectedConversation.turns].reverse().find(t => t.direction === 'incoming')
    : undefined;
  const hasSelection = isCustomerView ? !!selectedConversation : !!selectedMessage;

  const handleSelectConversation = (key: string) => {
    setSelectedConversationKey(key);
    setSelectedMessage(null);
    setReplyText('');
  };

  const handleSelectMessage = (msg: MarketplaceMessage) => {
    setSelectedMessage(msg);
    setSelectedConversationKey(null);
    setReplyText('');
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBag className="text-indigo-600 dark:text-indigo-400" size={24} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Marketplace Messages</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">eBay &amp; Amazon buyer communications</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw size={15} className={isRefreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Platform Tabs */}
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
          {(['ebay', 'amazon'] as Platform[]).map(platform => {
            const count = platform === 'ebay' ? ebayUnreadCount : amazonMessages.filter(m => !m.isRead).length;
            const pcfg = PLATFORM_CFG[platform];
            return (
              <button
                key={platform}
                onClick={() => {
                  setActivePlatform(platform);
                  setSelectedMessage(null);
                  setSelectedConversationKey(null);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activePlatform === platform
                    ? `border-indigo-600 ${pcfg.color}`
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {pcfg.label}
                {count > 0 && (
                  <span className="px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Connection Banner */}
        {!ebayStatusLoading && !isConnected && (
          <div className={`flex items-center justify-between p-4 rounded-lg border ${cfg.bg} ${cfg.border}`}>
            <div className="flex items-center gap-3">
              <AlertCircle size={18} className={cfg.color} />
              <div>
                <p className={`text-sm font-medium ${cfg.color}`}>
                  {cfg.label} account not connected
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {activePlatform === 'ebay'
                    ? 'Sign in with your eBay seller account to fetch messages via the eBay API.'
                    : 'Amazon SP-API integration coming soon.'}
                </p>
              </div>
            </div>
            {activePlatform === 'ebay' && (
              <button
                onClick={handleConnectEbay}
                disabled={connectingEbay}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-60 ${cfg.btnBg}`}
              >
                {connectingEbay ? (
                  <><Loader2 size={15} className="animate-spin" /> Connecting…</>
                ) : (
                  <>Connect {cfg.label}</>
                )}
              </button>
            )}
          </div>
        )}

        {isConnected && (
          <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={15} className="text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-700 dark:text-green-400 font-medium">
                {cfg.label} connected
                {ebayStatus?.userName && (
                  <span className="font-normal text-green-600 dark:text-green-500"> · {ebayStatus.userName}</span>
                )}
              </span>
            </div>
            <button
              onClick={() => disconnectEbayMutation.mutate()}
              disabled={disconnectEbayMutation.isPending}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
            >
              <Link2Off size={13} /> Disconnect
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-4 h-[calc(100vh-300px)] min-h-[480px]">
          {/* Message List Panel */}
          <div className={`flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${hasSelection ? 'hidden lg:flex lg:w-80 xl:w-96 flex-shrink-0' : 'flex-1'}`}>
            {/* Search + Filter */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search messages…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {activePlatform === 'ebay' && (
                <div className="flex gap-1">
                  {(['customer', 'system'] as MessageSource[]).map(src => {
                    const count = src === 'customer' ? conversations.length : ebaySystemMessages.length;
                    return (
                      <button
                        key={src}
                        onClick={() => {
                          setMessageSource(src);
                          setSelectedMessage(null);
                          setSelectedConversationKey(null);
                        }}
                        className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          messageSource === src
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-gray-50 dark:bg-gray-900/50'
                        }`}
                      >
                        {src === 'customer' ? 'Customer Messages' : 'eBay System'} ({count})
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-1">
                {(['all', 'unread', 'awaiting', 'replied'] as MessageFilter[]).map(f => (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md capitalize transition-colors ${
                      activeFilter === f
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* List Items */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {isCustomerView ? (
                filteredConversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <MessageSquare size={40} className="text-gray-300 dark:text-gray-600 mb-3" />
                    {!isConnected ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Connect your eBay account to see messages</p>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No conversations found</p>
                    )}
                  </div>
                ) : (
                  filteredConversations.map(convo => {
                    const lastTurn = convo.turns[convo.turns.length - 1];
                    return (
                      <button
                        key={convo.key}
                        onClick={() => handleSelectConversation(convo.key)}
                        className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                          selectedConversationKey === convo.key ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                        } ${convo.hasUnread ? 'border-l-2 border-indigo-500' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className={`text-sm truncate ${convo.hasUnread ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
                            {convo.buyerUsername}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0">{formatRelativeTime(convo.lastMessageAt)}</span>
                        </div>
                        {convo.itemTitle && (
                          <p className="text-xs mb-1 truncate text-gray-600 dark:text-gray-400">{convo.itemTitle}</p>
                        )}
                        {lastTurn && (
                          <p className="text-xs text-gray-400 truncate">
                            {lastTurn.direction === 'outgoing' ? 'You: ' : ''}
                            {lastTurn.body}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {convo.hasUnread ? (
                            <span className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400"><Mail size={11} /> Unread</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-gray-400"><MailOpen size={11} /> Read</span>
                          )}
                          {lastTurn?.direction === 'outgoing' ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><Reply size={11} /> Replied</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><Clock size={11} /> Awaiting reply</span>
                          )}
                          {lastTurn && lastTurn.attachments.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-gray-400"><Paperclip size={11} /> {lastTurn.attachments.length}</span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )
              ) : filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <MessageSquare size={40} className="text-gray-300 dark:text-gray-600 mb-3" />
                  {!isConnected && activePlatform === 'ebay' ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Connect your eBay account to see messages</p>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No messages found</p>
                  )}
                </div>
              ) : (
                filteredMessages.map(msg => (
                  <button
                    key={msg.id}
                    onClick={() => handleSelectMessage(msg)}
                    className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedMessage?.id === msg.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                    } ${!msg.isRead ? 'border-l-2 border-indigo-500' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className={`text-sm truncate ${!msg.isRead ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
                        {msg.senderName}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{formatRelativeTime(msg.receivedAt)}</span>
                    </div>
                    <p className={`text-xs mb-1 truncate ${!msg.isRead ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                      {msg.subject}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{msg.body}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {!msg.isRead ? (
                        <span className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400"><Mail size={11} /> Unread</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-gray-400"><MailOpen size={11} /> Read</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <p className="text-xs text-gray-400">
                {isCustomerView
                  ? `${filteredConversations.length} conversation${filteredConversations.length !== 1 ? 's' : ''}`
                  : `${filteredMessages.length} message${filteredMessages.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          {/* Detail Panel */}
          {isCustomerView && selectedConversation ? (
            <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setSelectedConversationKey(null)}
                  className="lg:hidden p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">{selectedConversation.buyerUsername}</h2>
                  {selectedConversation.itemTitle && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{selectedConversation.itemTitle}</p>
                  )}
                </div>
                {selectedConversation.itemId && (
                  <a
                    href={`https://www.ebay.com/itm/${selectedConversation.itemId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <ExternalLink size={13} />
                    Open in eBay
                  </a>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {selectedConversation.turns.map(turn => (
                  <div key={turn.id} className={`flex ${turn.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      turn.direction === 'outgoing'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}>
                      {turn.direction === 'incoming' && (
                        <p className="text-xs font-medium mb-1 text-indigo-600 dark:text-indigo-400">{turn.senderName}</p>
                      )}
                      {turn.body && <p className="text-sm whitespace-pre-wrap break-words">{turn.body}</p>}
                      {turn.attachments.length > 0 && (
                        <div className={`grid grid-cols-2 gap-2 ${turn.body ? 'mt-2' : ''}`}>
                          {turn.attachments.map((att, i) => (
                            <a key={i} href={att.url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={att.url}
                                alt={att.name}
                                className="rounded-md w-full h-24 object-cover border border-black/10 hover:opacity-90 transition-opacity"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                      <p className={`text-xs mt-1 ${turn.direction === 'outgoing' ? 'text-indigo-200 text-right' : 'text-gray-500 dark:text-gray-400'}`}>
                        {new Date(turn.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                {!selectedConversation.itemId || !lastIncomingTurn?.externalMessageId ? (
                  <p className="text-xs text-gray-400">This conversation can't be replied to directly via eBay (missing listing or message reference).</p>
                ) : (
                  <>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Reply</label>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={4}
                      placeholder={`Type your reply to ${selectedConversation.buyerUsername}…`}
                      className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">Reply sent via eBay messaging API</p>
                      <button
                        onClick={() => {
                          if (!selectedConversation.itemId || !lastIncomingTurn?.externalMessageId || !replyText.trim()) return;
                          replyMutation.mutate({
                            itemId: selectedConversation.itemId,
                            parentMessageId: lastIncomingTurn.externalMessageId,
                            recipientId: selectedConversation.buyerUsername,
                            body: replyText.trim(),
                          });
                        }}
                        disabled={!replyText.trim() || replyMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                      >
                        {replyMutation.isPending ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Reply size={14} />
                        )}
                        Send Reply
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : !isCustomerView && selectedMessage ? (
            <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="lg:hidden p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">{selectedMessage.subject}</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    From <span className="font-medium">{selectedMessage.senderName}</span>
                    {selectedMessage.orderId && (
                      <> · Order <span className="font-medium font-mono">{selectedMessage.orderId}</span></>
                    )}
                  </p>
                </div>
                <a
                  href="#"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  onClick={e => e.preventDefault()}
                >
                  <ExternalLink size={13} />
                  Open in {PLATFORM_CFG[selectedMessage.platform].label}
                </a>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {selectedMessage.itemTitle && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <ShoppingBag size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Re: <span className="font-medium text-gray-800 dark:text-gray-200">{selectedMessage.itemTitle}</span>
                    </span>
                  </div>
                )}

                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                    <p className={`text-xs font-medium mb-1 ${selectedMessage.isSystemMessage ? 'text-blue-600 dark:text-blue-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
                      {selectedMessage.senderName}
                    </p>
                    <p className="text-sm whitespace-pre-wrap break-words">{selectedMessage.body}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(selectedMessage.receivedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                {selectedMessage.isSystemMessage ? (
                  <p className="text-xs text-gray-400">eBay system messages can't be replied to.</p>
                ) : (
                  <>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Reply</label>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={4}
                      placeholder={`Type your reply to ${selectedMessage.senderName}…`}
                      className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">
                        Reply sent via {PLATFORM_CFG[selectedMessage.platform].label} messaging API
                      </p>
                      <button
                        disabled={!replyText.trim()}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                      >
                        <Reply size={14} />
                        Send Reply
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="hidden lg:flex flex-1 items-center justify-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <MessageSquare size={48} className="text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Select a {isCustomerView ? 'conversation' : 'message'} to read</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
